import React from 'react';
import styled from 'styled-components';
import { theme } from '../styled/theme';
import { Container, GradientTitle, TextMuted, Grid2, Card, Card2, ButtonLinkPrimary, ButtonLink, Flex, Chip } from '../styled/BaseComponents';

const PaymentsWrapper = styled.section`
  margin-top: 1.5rem;
`;

const IBANCard = styled(Card)`
  padding: 1rem;
`;

const IBANTitle = styled(GradientTitle)`
  margin: 0 0 6px 0;
`;

const PillsContainer = styled(Flex)`
  flex-wrap: wrap;
  margin-top: 0.75rem;
`;

const ItemCard = styled(Card)`
  padding: 0.75rem;
`;

const ItemTitle = styled.div`
  font-weight: 700;
  color: ${theme.colors.text};
`;

const ItemDescription = styled(TextMuted)`
  font-size: 13px;
  margin-top: 4px;
  line-height: 1.6;
`;

const ActionsContainer = styled(Flex)`
  flex-wrap: wrap;
  margin-top: 0.75rem;
`;

const AccountCard = styled(Card)`
  padding: 1rem;
`;

const AccountTitle = styled.div`
  font-weight: 700;
  color: ${theme.colors.text};
`;

const AccountDetails = styled(Card2)`
  padding: 10px 12px;
  margin-top: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  color: ${theme.colors.text};
`;

const AccountRow = styled.div`
  color: ${theme.colors.text};
`;

const AccountCode = styled.code`
  color: ${theme.colors.text};
`;

const SWIFTCard = styled(Card)`
  padding: 1rem;
`;

const SWIFTTitle = styled.div`
  font-weight: 700;
  color: ${theme.colors.text};
`;

const SWIFTDetails = styled(Card2)`
  padding: 10px 12px;
  margin-top: 8px;
  color: ${theme.colors.text};
`;

const SWIFTRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  color: ${theme.colors.text};
`;

const SWIFTLabel = styled.span`
  color: ${theme.colors.text};
`;

const SWIFTValue = styled.strong`
  color: ${theme.colors.text};
`;

const PaymentsContainer = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1.1fr 1fr;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const SWIFTSection = styled.div`
  display: grid;
  gap: 16px;
`;

const Item = ({ t, s }) => (
  <ItemCard>
    <ItemTitle>{t}</ItemTitle>
    <ItemDescription>{s}</ItemDescription>
  </ItemCard>
);

export const PaymentsIBAN = () => {
  const pill = (t) => <Chip>{t}</Chip>;

  return (
    <PaymentsWrapper id="iban">
      <Container>
        <PaymentsContainer>
          <IBANCard>
          <IBANTitle>IBAN Accounts & International Payments</IBANTitle>
          <TextMuted style={{ marginTop: 4 }}>
            Open dedicated EUR IBANs (SEPA / SEPA Instant) and send/receive international transfers via SWIFT in
            multiple currencies (USD, GBP, CAD, CHF, etc.). Saved beneficiaries, standard payment references, and
            AML/CTF controls.
          </TextMuted>
          <PillsContainer gap="0.5rem">
            {pill('EUR IBAN')}
            {pill('SEPA Instant')}
            {pill('SWIFT MT103')}
            {pill('Multi-currency')}
            {pill('Beneficiaries')}
            {pill('AML checks')}
          </PillsContainer>
          <Grid2 style={{ marginTop: '0.75rem' }}>
            <Item t="Create/Link EUR IBAN" s="Generate a dedicated IBAN or link an existing account for SEPA (SCT/Instant) collections." />
            <Item t="SWIFT Transfer" s="Send MT103 wires to 100+ countries, with estimated fees and timing; track payment status." />
            <Item t="Beneficiary Directory" s="Save company and personal accounts; verify IBAN/BIC; set recurring templates." />
            <Item t="Limits & Approvals" s="Daily thresholds, multi-user authorization, and 4-eyes approval workflow." />
          </Grid2>
          <ActionsContainer gap="0.5rem">
            <ButtonLinkPrimary to="/sign-in">Open EUR IBAN</ButtonLinkPrimary>
            <ButtonLink to="/sign-in">New transfer</ButtonLink>
            <ButtonLink to="/sign-in">Add beneficiary</ButtonLink>
          </ActionsContainer>
        </IBANCard>
        <SWIFTSection>
          <AccountCard>
            <AccountTitle>EUR Account — IBAN</AccountTitle>
            <AccountDetails>
              <AccountRow>
                IBAN: <AccountCode>IT** ** ** ** ** ** *</AccountCode>
              </AccountRow>
              <AccountRow>
                BIC: <AccountCode>**ITMMXXX</AccountCode>
              </AccountRow>
              <AccountRow>
                Account Holder: <AccountCode>Custodiy Financial Services Ltd</AccountCode>
              </AccountRow>
            </AccountDetails>
            <Flex gap="0.5rem" style={{ marginTop: '0.75rem', flexWrap: 'wrap' }}>
              <ButtonLink to="/sign-in">Copy IBAN</ButtonLink>
              <ButtonLink to="/sign-in">Show details</ButtonLink>
            </Flex>
          </AccountCard>
          <SWIFTCard>
            <SWIFTTitle>SWIFT Wire — Multi-currency</SWIFTTitle>
            <TextMuted style={{ fontSize: 13, marginTop: 6 }}>Example estimate:</TextMuted>
            <SWIFTDetails>
              <SWIFTRow>
                <SWIFTLabel>Currency</SWIFTLabel>
                <SWIFTValue>USD</SWIFTValue>
              </SWIFTRow>
              <SWIFTRow>
                <SWIFTLabel>Amount</SWIFTLabel>
                <SWIFTValue>25,000.00</SWIFTValue>
              </SWIFTRow>
              <SWIFTRow>
                <SWIFTLabel>Estimated Fees</SWIFTLabel>
                <SWIFTValue>$ 18.00</SWIFTValue>
              </SWIFTRow>
              <SWIFTRow>
                <SWIFTLabel>ETA</SWIFTLabel>
                <SWIFTValue>T+1 / T+2</SWIFTValue>
              </SWIFTRow>
            </SWIFTDetails>
            <Flex gap="0.5rem" style={{ marginTop: '0.75rem', flexWrap: 'wrap' }}>
              <ButtonLinkPrimary to="/sign-in">Create wire</ButtonLinkPrimary>
              <ButtonLink to="/sign-in">History</ButtonLink>
            </Flex>
          </SWIFTCard>
        </SWIFTSection>
        </PaymentsContainer>
      </Container>
    </PaymentsWrapper>
  );
};

export default PaymentsIBAN;

